'use client';

import React from 'react';
import { cn } from '@/lib/utils';
import { InfoTooltip } from '@/components/InfoTooltip';

/**
 * Shared card shell for Agent Insights sections: rounded/bordered container →
 * header (icon + title + InfoTooltip [+ headerRight] [+ headerExtra]) →
 * isLoading skeleton / isEmpty state / real content.
 *
 * Header layout is intentionally caller-controlled via `headerClassName` /
 * `titleGroupClassName` since card headers vary (gray band vs plain block,
 * fixed height vs padding-only, single row vs row + subtitle) across the
 * dashboard — this lets each call site reproduce its exact prior markup.
 */
export interface InsightCardProps {
  title: React.ReactNode;
  icon?: React.ReactNode;
  tooltip?: React.ReactNode;
  tooltipIconClassName?: string;
  /** Rendered alongside the title group, on the same header row (e.g. a search input). */
  headerRight?: React.ReactNode;
  /** Rendered below the title row, still inside the header block (e.g. a subtitle or stat). */
  headerExtra?: React.ReactNode;
  isLoading?: boolean;
  loadingSlot?: React.ReactNode;
  isEmpty?: boolean;
  emptySlot?: React.ReactNode;
  /**
   * A failed fetch is not an empty one. Without this the card falls through to
   * `emptySlot` and tells the reader they have no data, when the truth is we
   * could not load it.
   */
  isError?: boolean;
  /** Defaults to the standard copy; pass a node only to name the thing that failed. */
  errorSlot?: React.ReactNode;
  children?: React.ReactNode;
  /** Extra classes for the outer card container (layout only — flex/height, not tokens). */
  className?: string;
  /** Full class list for the header block (padding, background, height, flex layout). */
  headerClassName?: string;
  /** Full class list for the title + tooltip group. */
  titleGroupClassName?: string;
  titleClassName?: string;
}

/** Same shape as the cards' `emptySlot`, so a failure reads as a state of this
 *  card rather than a different kind of surface. Exported for the few Overview
 *  surfaces that are not an `InsightCard` but share its states. */
export function InsightCardError() {
  return (
    <div
      role="status"
      className="flex flex-1 items-center justify-center p-6 text-center ds-text-body-sm-medium text-ds-text-subtle"
    >
      Couldn&apos;t load this. Please try again in a moment. If it keeps
      happening, reach out to support.
    </div>
  );
}

export function InsightCard({
  title,
  icon,
  tooltip,
  tooltipIconClassName,
  headerRight,
  headerExtra,
  isLoading,
  loadingSlot,
  isEmpty,
  emptySlot,
  isError,
  errorSlot,
  children,
  className,
  headerClassName,
  titleGroupClassName = 'flex items-center gap-2',
  titleClassName,
}: InsightCardProps) {
  return (
    <div
      className={cn(
        'rounded-lg border border-ds-border-subtlest bg-ds-surface-white overflow-hidden',
        className
      )}
    >
      <div
        className={cn('border-b border-ds-border-subtlest', headerClassName)}
      >
        <div className={titleGroupClassName}>
          {icon}
          <h3 className={titleClassName}>{title}</h3>
          {tooltip && (
            <InfoTooltip
              className="shrink-0"
              iconClassName={tooltipIconClassName}
              content={tooltip}
            />
          )}
        </div>
        {headerRight}
        {headerExtra}
      </div>
      {isError
        ? (errorSlot ?? <InsightCardError />)
        : isLoading
          ? loadingSlot
          : isEmpty
            ? emptySlot
            : children}
    </div>
  );
}
